import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Typography, Chip, Tooltip } from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import GetAppIcon from '@material-ui/icons/GetApp';
import SettingsIcon from '@material-ui/icons/Settings';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import TableSite from '../../../components/common/Table';
import ReporteriaAPI from '../reporteria-api';
import useReportHistory, { reportDateDetails, reportErrorMessage } from './useReportHistory';
import { useHistory } from 'react-router-dom';
import '../../../assets/styles/content/reporteria.scss';

const REPORT_TYPES = {
  'register': 'Registro',
  'activity': 'Actividad',
  'custom': 'Personalizado'
};

const FREQUENCIES = {
  'once': 'Una vez',
  'weekly': 'Semanal',
  'monthly': 'Mensual'
};

const STATUS = {
  'pending': 'Pendiente',
  'processing': 'En proceso',
  'completed': 'Completado',
  'failed': 'Fallido',
  'deferred': 'Diferido'
};

const STATUS_COLORS = {
  'pending': '#bdbdbd',      // gris
  'processing': '#ffb300',   // amarillo
  'completed': '#43a047',    // verde
  'failed': '#e53935',       // rojo
  'deferred': '#38bdf8'      // cyan
};

const ReportsList = () => {
  const history = useHistory();
  const [pageNumber, setPageNumber] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const [actionError, setActionError] = useState('');
  const mounted = useRef(true);
  const {
    reports, reportValidations, totalPages, isLoadingReports, isRefreshing,
    errorMessage, pollingPaused, refreshReports,
  } = useReportHistory(pageNumber);
  const filteredReports = useMemo(() => filterReports(searchValue, reports), [reports, searchValue]);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // Columnas para la tabla
  const columns = [
    {
      Header: 'Nombre',
      accessor: 'name',
      Cell: ({ row }) => {
        const config = row.original.configuration || {};
        // El backend envía 'nombre' (español), no 'name' (inglés)
        const name = row.original.nombre || config.name || row.original.name || `Reporte ${REPORT_TYPES[row.original.tipo] || row.original.tipo}`;
        const description = row.original.description || config.description || '';
        return (
          <div style={{ textAlign: 'left' }}>
            <strong>{name}</strong>
            {description && (
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                {description}
              </div>
            )}
          </div>
        );
      },
    },
    {
      Header: 'Fecha de ejecución',
      accessor: 'fechaEjecucion', // Nuevo accessor calculado
      Cell: ({ row }) => {
        const { timestamp: fechaEjecucion, label: tipoFecha } = reportDateDetails(row.original);

        const fechaFormateada = fechaEjecucion 
          ? new Date(fechaEjecucion).toLocaleString()
          : 'Sin fecha';

        return (
          <div style={{ textAlign: 'left' }}>
            <div>{fechaFormateada}</div>
            {tipoFecha && (
              <div style={{ fontSize: '11px', color: '#666', fontStyle: 'italic' }}>
                {tipoFecha}
              </div>
            )}
          </div>
        );
      },
    },
    {
      Header: 'Tipo',
      accessor: 'tipo',
      Cell: ({ value }) => (
        <div style={{ textAlign: 'left' }}>
          {REPORT_TYPES[value] || value}
        </div>
      ),
    },
    {
      Header: 'Creado por',
      accessor: 'creadoPor',
      Cell: ({ value }) => (
        <div style={{ textAlign: 'left' }}>
          {value ? value.nombre : 'No disponible'}
        </div>
      ),
    },
    {
      Header: 'Frecuencia',
      accessor: 'frecuencia',
      Cell: ({ row }) => {
        const tipoFrecuencia = row.original.frecuencia?.tipo;
        let frecuenciaTexto = 'No programado';
        
        if (tipoFrecuencia && tipoFrecuencia !== 'instant') {
          frecuenciaTexto = FREQUENCIES[tipoFrecuencia] || tipoFrecuencia;
        }
        
        return (
          <div style={{ textAlign: 'left' }}>
            {frecuenciaTexto}
          </div>
        );
      },
    },
    {
      Header: 'Estado',
      accessor: 'estado',
      Cell: ({ value, row }) => {
        const log = row.original;
        const isDeferredStatus = value === 'deferred';
        const statusLabel = STATUS[value] || value || 'Sin estado';
        const failureMessage = (value === 'failed' || isDeferredStatus ? log.errorMessage : '') || reportValidations[log.id]?.downloadError ||
          (value === 'failed' ? 'No se pudo generar el reporte.' : '');
        const chipContent = (
          <Chip
            label={
              <Box display="flex" alignItems="center">
                {isDeferredStatus && <AccessTimeIcon style={{ fontSize: 16, marginRight: 4, color: '#fff' }} />}
                {statusLabel}
              </Box>
            }
            style={{
              backgroundColor: STATUS_COLORS[value] || '#bdbdbd',
              color: '#fff',
              minWidth: isDeferredStatus ? 120 : 100,
              justifyContent: 'center'
            }}
            size="small"
          />
        );
        return (
          <div className="report-status">
            {chipContent}
            {failureMessage && (
              <Tooltip title={failureMessage} arrow>
                <Typography variant="caption" component="p" className="report-status__error" tabIndex={0}>
                  {failureMessage}
                </Typography>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      Header: 'Configuración',
      accessor: 'configuracionReporte.id',
      Cell: ({ row }) => {
        const logId = row.original.id;
        const configId = row.original.configuracionReporte?.id;
        const validation = reportValidations[logId];
        const canEdit = validation?.canEdit || false;
        const isDisabled = !configId || !canEdit;
        
        return (
          <div style={{ textAlign: 'center' }}>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              className="action-button"
              onClick={() => openReportConfig(configId)}
              disabled={isDisabled}
              title={isDisabled ? 'Configuración no disponible' : 'Ver configuración'}
            >
              <SettingsIcon fontSize="small" className="button-icon" />
              Ver
            </Button>
          </div>
        );
      },
    },
    {
      Header: 'Descargar',
      accessor: 'url',
      Cell: ({ value, row }) => {
        const logId = row.original.id;
        const validation = reportValidations[logId];
        const canDownload = validation?.canDownload || false;
        const hasUrl = !!value;
        const isEnabled = canDownload && hasUrl;

        const handleDownloadClick = async () => {
          if (!isEnabled) return;
          try {
            const response = await ReporteriaAPI.validateDownload(logId);
            const freshUrl = response.data?.data?.url;
            if (!mounted.current) return;
            if (freshUrl && response.data?.data?.canDownload === true) {
              setActionError('');
              window.open(freshUrl, '_blank', 'noopener,noreferrer');
            } else {
              setActionError(response.data?.data?.reason || 'No se pudo obtener el archivo para descarga.');
            }
          } catch (error) {
            if (mounted.current) setActionError(reportErrorMessage(error, 'No se pudo descargar el reporte.'));
          }
        };

        return (
          <div style={{ textAlign: 'center' }}>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              className="action-button"
              onClick={handleDownloadClick}
              disabled={!isEnabled}
              title={isEnabled ? 'Descargar reporte' : 'Archivo no disponible'}
            >
              <GetAppIcon fontSize="small" className="button-icon" />
              {isEnabled ? 'Descargar' : 'No disponible'}
            </Button>
          </div>
        );
      },
    },
  ];

  const onChangePage = (event, newPage) => {
    setPageNumber(newPage);
    setActionError('');
  };

  // Función para crear un nuevo reporte
  const createNewReport = () => {
    history.push('/reporteria/crear');
  };

  // Función para abrir la configuración de un reporte
  const openReportConfig = (reportId) => {
    history.push(`/reporteria/editar/${reportId}`);
  };

  // Función para filtrar reportes
  function filterReports(searchTerm, reportsList) {
    if (!searchTerm || typeof searchTerm !== 'string' || !searchTerm.trim()) {
      return reportsList;
    }

    const term = searchTerm.toLowerCase();
    
    return reportsList.filter(report => {
      // Buscar en nombre
      const nombre = (report.nombre || report.name || '').toLowerCase();
      
      // Buscar en tipo
      const tipo = (REPORT_TYPES[report.tipo] || report.tipo || '').toLowerCase();
      
      // Buscar en estado (incluye el nuevo estado "diferido")
      const estado = (STATUS[report.estado] || report.estado || '').toLowerCase();
      
      // Buscar en frecuencia
      const frecuencia = report.frecuencia && report.frecuencia.tipo 
        ? (FREQUENCIES[report.frecuencia.tipo] || report.frecuencia.tipo || '').toLowerCase()
        : 'no programado';
      
      // Buscar en fecha de ejecución (usando la misma lógica que la columna)
      const { timestamp: fechaEjecucion } = reportDateDetails(report);
      
      const fecha = fechaEjecucion 
        ? new Date(fechaEjecucion).toLocaleString().toLowerCase()
        : '';
      
      // Buscar en descripción
      const descripcion = (report.description || '').toLowerCase();
      
      // Buscar en nombre del creador
      const creador = report.creadoPor ? (report.creadoPor.nombre || '').toLowerCase() : '';

      return nombre.includes(term) || 
             tipo.includes(term) || 
             estado.includes(term) || 
             frecuencia.includes(term) || 
             fecha.includes(term) ||
             descripcion.includes(term) ||
             creador.includes(term);
    });
  };

  // Función para buscar
  const handleSearch = (value) => {
    // Si es un evento, extraer el valor del input
    const searchTerm = typeof value === 'string' ? value : value?.target?.value || '';
    
    setSearchValue(searchTerm);
  };

  return (
    <>
      <div className="user-container reporteria-container">
        {(errorMessage || actionError) && (
          <Typography color="error" role="alert" style={{ marginBottom: 16 }}>
            {actionError || errorMessage}
          </Typography>
        )}
        {pollingPaused && (
          <Typography variant="body2" style={{ marginBottom: 16 }}>
            La actualización automática se pausó. Usa el botón de actualizar para consultar el estado.
          </Typography>
        )}
        {/* Tabla de reportes con opciones en la misma barra de título */}
        <TableSite
          data={filteredReports}
          columns={columns}
          isLoadingData={isLoadingReports}
          tableName={
            <>
              Historial de reportes
              {searchValue && (
                <span style={{ fontSize: '12px', color: '#666', marginLeft: '16px' }}>
                  ({filteredReports.length} de {reports.length} logs)
                </span>
              )}
            </>
          }
          totalPages={totalPages}
          currentPage={pageNumber}
          onChangePage={onChangePage}
          onSearch={handleSearch}
          buttonLabel="Crear reporte"
          onClickButton={createNewReport}
          isDynamic
        />

        {/* Botón de actualizar flotante */}
        <Button 
          variant="outlined"
          color="primary" 
          size="small"
          className="refresh-floating-button"
          aria-label="Actualizar historial de reportes"
          title="Actualizar historial de reportes"
          onClick={() => {
            setSearchValue('');
            setActionError('');
            refreshReports({ resetPolling: true });
          }}
          disabled={isRefreshing}
        >
          <RefreshIcon 
            className={isRefreshing ? 'spinning' : ''}
          />
        </Button>
      </div>

    </>
  );
};

export default ReportsList;
